// src/pages/DiamondList.jsx
import React, { useEffect, useMemo, useRef, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Link, useNavigate } from "react-router-dom";
import Select from "react-select";
import {
  FaPencilAlt,
  FaTrashAlt,
  FaFilter,
  FaTimesCircle,
  FaGem,
  FaPlus,
  FaPowerOff,
} from "react-icons/fa";
import { FiRotateCcw } from "react-icons/fi";
import toast from "react-hot-toast";

import { useDebounce } from "../hooks/useDebounced";
import {
  useGetAllDiamondsQuery,
  useDeleteDiamondMutation,
  useActivateDiamondMutation,
  useDeactivateDiamondMutation,
} from "../toolkitServices/diamondApi";

import ConfirmDialog from "../components/ConfirmDialog";

/* ------------ Tiny reusable spinner ------------ */
const Spinner = ({ size = 16, className = "" }) => (
  <span
    className={`inline-block animate-spin rounded-full border-2 border-current border-t-transparent ${className}`}
    style={{ width: size, height: size }}
  />
);

export default function DiamondList() {
  const navigate = useNavigate();
  const searchRef = useRef(null);

  // search + debounce
  const [search, setSearch] = useState("");
  const debouncedSearch = useDebounce(search, 400);

  // pagination
  const [page, setPage] = useState(1);
  const [limit, setLimit] = useState(10);

  // filters (applied)
  const [filters, setFilters] = useState({
    status: "", // "", "Active", "Inactive"
  });

  // filter modal temp
  const [isFilterOpen, setIsFilterOpen] = useState(false);
  const [tempFilters, setTempFilters] = useState(filters);

  const openFilters = () => {
    setTempFilters(filters);
    setIsFilterOpen(true);
  };

  // status -> activeOnly param for API (your API expects "true" | "" currently)
  const activeOnly = useMemo(() => {
    if (filters.status === "Active") return "true";
    if (filters.status === "Inactive") return "false";
    return "";
  }, [filters.status]);

  // query args (keeping your q param)
  const queryArgs = useMemo(
    () => ({
      page,
      limit,
      q: debouncedSearch,
      activeOnly,
    }),
    [page, limit, debouncedSearch, activeOnly]
  );

  const { data, isLoading, isFetching, isError, error, isSuccess } = useGetAllDiamondsQuery(
    queryArgs,
    { refetchOnMountOrArgChange: true }
  );

  // local rows (patch after delete/toggle)
  const [rows, setRows] = useState([]);

  useEffect(() => {
    if (isSuccess) setRows(Array.isArray(data?.data) ? data.data : []);
  }, [isSuccess, data]);

  // totals
  const total = data?.total ?? data?.pagination?.totalDocs ?? rows.length ?? 0;
  const totalPages =
    data?.pages ??
    data?.pagination?.totalPages ??
    Math.max(1, Math.ceil((Number(total) || 0) / (Number(limit) || 10)));

  // actions
  const [deleteDiamond, delState] = useDeleteDiamondMutation();
  const [activateDiamond, actState] = useActivateDiamondMutation();
  const [deactivateDiamond, deactState] = useDeactivateDiamondMutation();

  // confirm dialog
  const [deleteDlg, setDeleteDlg] = useState({ open: false, row: null });
  const openDelete = (row) => setDeleteDlg({ open: true, row });
  const closeDelete = () => setDeleteDlg({ open: false, row: null });

  // row loaders
  const [togglingId, setTogglingId] = useState(null);
  const [deletingId, setDeletingId] = useState(null);

  const confirmDelete = async () => {
    const row = deleteDlg.row;
    if (!row?._id) {
      toast.error("Invalid diamond id");
      return closeDelete();
    }
    try {
      setDeletingId(row._id);
      await deleteDiamond(row._id).unwrap();
      setRows((prev) => prev.filter((x) => String(x._id) !== String(row._id)));
      toast.success("Deleted");
      closeDelete();
    } catch (e) {
      toast.error(e?.data?.msg || e?.data?.message || "Delete failed");
    } finally {
      setDeletingId(null);
    }
  };

  const onToggleActive = async (row) => {
    try {
      if (!row?._id) return;
      setTogglingId(row._id);

      if (row.isActive) await deactivateDiamond(row._id).unwrap();
      else await activateDiamond(row._id).unwrap();

      setRows((prev) =>
        prev.map((x) =>
          String(x._id) === String(row._id) ? { ...x, isActive: !row.isActive } : x
        )
      );
      toast.success(row.isActive ? "Marked inactive" : "Marked active");
    } catch (e) {
      toast.error(e?.data?.msg || e?.data?.message || "Update failed");
    } finally {
      setTogglingId(null);
    }
  };

  const resetAll = () => {
    setSearch("");
    setFilters({ status: "" });
    setPage(1);
  };

  // chips
  const chips = useMemo(() => {
    const arr = [];
    if (search?.trim()) arr.push({ key: "search", label: `Search: ${search.trim()}` });
    if (filters.status) arr.push({ key: "status", label: `Status: ${filters.status}` });
    return arr;
  }, [search, filters.status]);

  const clearChip = (key) => {
    if (key === "search") setSearch("");
    if (key === "status") setFilters((f) => ({ ...f, status: "" }));
  };

  const paginationSelectStyles = {
    control: (base, state) => ({
      ...base,
      minHeight: 32,
      height: 32,
      borderRadius: 6,
      borderColor: state.isFocused ? "#9481ce" : "#e2e8f0",
      boxShadow: state.isFocused
        ? "0 0 0 1px rgba(148,129,206,.25)"
        : "none",
      fontSize: 10,
    }),
    valueContainer: (base) => ({ ...base, height: 32, padding: "0 5px" }),
    indicatorsContainer: (base) => ({ ...base, height: 32 }),
    dropdownIndicator: (base) => ({ ...base, padding: 4 }),
    indicatorSeparator: () => ({ display: "none" }),
    menu: (base) => ({ ...base, zIndex: 70, fontSize: 10 }),
    menuList: (base) => ({ ...base, maxHeight: 160, padding: 2 }),
    option: (base, state) => ({
      ...base,
      padding: "6px 8px",
      backgroundColor: state.isSelected
        ? "#9481ce"
        : state.isFocused
          ? "#ede9fe"
          : "white",
      color: state.isSelected ? "white" : "#334155",
    }),
    menuPortal: (base) => ({ ...base, zIndex: 9999 }),
  };

  // ⌨️ Ctrl+P + Cmd+P => Create diamond (suppress print dialog)
  useEffect(() => {
    const onKeyDown = (e) => {
      const isCtrlP = e.ctrlKey && (e.key === "p" || e.key === "P");
      const isCmdP = e.metaKey && (e.key === "p" || e.key === "P");
      if (isCtrlP || isCmdP) {
        e.preventDefault();
        navigate("/masters/diamond-master-list/new-diamond");
      }
      if (e.ctrlKey && (e.key === "f" || e.key === "F")) {
        e.preventDefault();
        searchRef.current?.focus();
      }
    };
    window.addEventListener("keydown", onKeyDown);
    return () => window.removeEventListener("keydown", onKeyDown);
  }, [navigate]);

  // when search/filter/limit changes => page 1
  useEffect(() => {
    setPage(1);
  }, [debouncedSearch, filters.status, limit]);

  return (
    <div className="relative flex h-[calc(100vh-140px)] flex-col overflow-hidden rounded-2xl bg-white shadow-lg md:h-[calc(100vh-70px)]">
      {/* Global overlay loader (first load) */}
      {isLoading && !rows.length && (
        <div className="absolute inset-0 z-30 flex items-center justify-center bg-white/60">
          <div className="flex flex-col items-center gap-2 text-slate-600 text-xs sm:text-sm">
            <Spinner size={22} />
            <span>Loading diamonds…</span>
          </div>
        </div>
      )}

      {/* Header */}
      <div className="flex shrink-0 items-center justify-between gap-3 bg-[#9481ce] px-3 py-2.5 text-white shadow-sm sm:px-4">
        <div className="flex min-w-0 items-center gap-2.5">
          <span className="inline-flex h-9 w-9 shrink-0 items-center justify-center rounded-xl bg-white/15 ring-1 ring-white/20">
            <FaGem className="text-sm" />
          </span>
          <div className="min-w-0">
            <div className="flex items-center gap-2">
              <h2 className="truncate text-[15px] font-semibold tracking-tight sm:text-lg">
                Diamond List
              </h2>
              <span className="text-[11px] font-semibold text-violet-100/90">
                {isFetching ? "refreshing…" : `(${total})`}
              </span>
            </div>
            <p className="hidden truncate text-[11px] text-violet-50 sm:block">
              Manage diamonds, shape, color, clarity and status
            </p>
          </div>

          {isFetching && rows.length > 0 && (
            <span className="hidden items-center gap-1 rounded-full bg-white/10 px-2 py-0.5 text-[10px] sm:inline-flex">
              <Spinner size={10} /> refreshing…
            </span>
          )}
        </div>

        <div className="flex items-center gap-2">
          <span className="hidden lg:inline text-[11px] text-violet-100/90">
            Ctrl + P to create new
          </span>
          <Link
            to="/masters/diamond-master-list/new-diamond"
            className="inline-flex h-9 items-center gap-2 rounded-xl bg-white px-3 text-[11px] font-semibold text-[#6b4fd8] shadow-sm transition hover:bg-violet-50 sm:rounded-full"
            title="Ctrl + P"
          >
            <FaPlus className="text-[10px]" />
            <span className="hidden sm:inline">Add New</span>
            <span className="sm:hidden">Add</span>
          </Link>
        </div>
      </div>

      {/* Filters bar */}
      <div className="shrink-0 border-b bg-slate-50/80 px-3 py-2 sm:px-4">
        {/* Mobile */}
        <div className="flex items-center gap-2 sm:hidden">
          <input
            ref={searchRef}
            type="text"
            placeholder="Search diamond…"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            className="h-9 min-w-0 flex-1 rounded-lg border border-slate-200 bg-white px-3 text-[11px] text-gray-800 outline-none focus:ring-1 focus:ring-violet-400"
          />
          <button
            onClick={openFilters}
            className="inline-flex h-9 flex-shrink-0 items-center gap-1 rounded-lg border border-violet-100 bg-white px-3 text-[11px] font-semibold text-[#6b59c6] shadow-sm hover:bg-violet-50"
            title="Filters"
          >
            <FaFilter /> Filters
          </button>
        </div>

        {/* Desktop */}
        <div className="hidden items-end gap-2 sm:flex">
          <div className="flex w-[150px] flex-col gap-[1px]">
            <label className="text-[11px] font-medium text-slate-600">Status</label>
            <select
              value={filters.status}
              onChange={(e) => setFilters((f) => ({ ...f, status: e.target.value }))}
              className="h-9 rounded-lg border border-slate-200 bg-white px-3 text-[11px] text-gray-800 outline-none focus:ring-1 focus:ring-violet-400"
              title="Filter by status"
            >
              <option value="">All</option>
              <option value="Active">Active</option>
              <option value="Inactive">Inactive</option>
            </select>
          </div>

          <div className="flex min-w-[240px] flex-1 flex-col gap-[1px]">
            <label className="text-[11px] font-medium text-slate-600">Search</label>
            <input
              ref={searchRef}
              type="text"
              placeholder="Search diamond…"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              className="h-9 rounded-lg border border-slate-200 bg-white px-3 text-[11px] text-gray-800 outline-none focus:ring-1 focus:ring-violet-400"
            />
          </div>

          <div className="flex w-auto flex-col gap-[1px]">
            <label className="text-[11px] font-medium text-slate-600 invisible">
              Filters
            </label>
            <button
              onClick={openFilters}
              className="inline-flex h-9 items-center gap-1 rounded-lg border border-violet-100 bg-white px-3 text-[11px] font-semibold text-[#6b59c6] shadow-sm hover:bg-violet-50"
            >
              <FaFilter /> Filters
            </button>
          </div>
        </div>
      </div>

      {/* Error bar */}
      {isError && (
        <div className="shrink-0 border-b bg-red-50 px-3 py-1.5 text-xs text-red-600 sm:px-4">
          {error?.data?.msg || error?.data?.message || "Failed to load diamonds"}
        </div>
      )}

      {/* Chips */}
      {chips.length > 0 && (
        <div className="flex shrink-0 items-center gap-2 border-b bg-slate-50 px-3 py-1.5 sm:px-4">
          <div className="flex min-w-0 flex-nowrap items-center gap-2 overflow-x-auto pr-2 [-ms-overflow-style:none] [scrollbar-width:none] [&::-webkit-scrollbar]:hidden">
            {chips.map((c) => (
              <span
                key={c.key}
                className="inline-flex flex-shrink-0 items-center gap-2 whitespace-nowrap rounded-full border bg-white px-2 py-1 text-[11px] shadow-sm"
              >
                {c.label}
                <button
                  className="text-slate-500 hover:text-red-500"
                  onClick={() => clearChip(c.key)}
                  title="Clear"
                >
                  <FaTimesCircle />
                </button>
              </span>
            ))}
          </div>

          <button
            className="ml-auto flex-shrink-0 text-[11px] text-[#6b59c6] hover:underline"
            onClick={resetAll}
            title="Reset all"
          >
            Reset
          </button>
        </div>
      )}

      {/* Table */}
      <div className="relative min-h-0 flex-1 overflow-x-auto overflow-y-auto overscroll-x-contain bg-white [-webkit-overflow-scrolling:touch]">
        <table className="w-full min-w-[900px] text-left text-xs sm:text-sm">
          <thead className="sticky top-0 z-20 bg-[#efeafd] text-[10px] uppercase text-[#4b3d91] shadow-sm sm:text-[11px]">
            <tr>
              <th className="px-3 sm:px-4 py-2">Name</th>
              <th className="px-3 sm:px-4 py-2">Shape</th>
              <th className="px-3 sm:px-4 py-2">Color</th>
              <th className="px-3 sm:px-4 py-2">Clarity</th>
              <th className="px-3 sm:px-4 py-2">Deductable</th>
              <th className="px-3 sm:px-4 py-2">Status</th>
              <th className="sticky right-0 z-30 w-[82px] min-w-[82px] border-l border-violet-200 bg-[#ddd5f7] px-1 py-2 text-center shadow-[-6px_0_10px_-10px_rgba(15,23,42,0.45)] sm:w-[104px] sm:min-w-[104px] sm:px-2">
                <span className="sm:hidden">Act</span>
                <span className="hidden sm:inline">Action</span>
              </th>
            </tr>
          </thead>

          <tbody className="bg-white text-gray-700">
            {/* skeleton */}
            {isLoading &&
              Array.from({ length: 6 }).map((_, i) => (
                <tr key={`sk-${i}`} className="border-t">
                  {Array.from({ length: 6 }).map((__, j) => (
                    <td key={j} className="px-3 sm:px-4 py-3">
                      <div className="h-4 w-32 bg-gray-200 rounded animate-pulse" />
                    </td>
                  ))}
                  <td className="sticky right-0 z-10 w-[82px] min-w-[82px] border-l border-violet-100 bg-violet-50 px-1 py-3 shadow-[-6px_0_10px_-10px_rgba(15,23,42,0.35)] sm:w-[104px] sm:min-w-[104px] sm:px-2">
                    <div className="ml-auto h-7 w-20 rounded bg-gray-200 animate-pulse sm:w-24" />
                  </td>
                </tr>
              ))}

            {/* empty */}
            {!isLoading && !isError && rows.length === 0 && (
              <tr>
                <td colSpan={7} className="text-center text-gray-500 py-6">
                  No matching records found.
                </td>
              </tr>
            )}

            <AnimatePresence>
              {!isLoading &&
                rows.map((item, index) => {
                  const id = item?._id;
                  const isToggling =
                    togglingId === id && (actState.isLoading || deactState.isLoading);
                  const isDeleting = deletingId === id && delState.isLoading;

                  return (
                    <motion.tr
                      key={id || index}
                      initial={{ opacity: 0, y: 6 }}
                      animate={{ opacity: 1, y: 0 }}
                      exit={{ opacity: 0, y: -6 }}
                      transition={{ duration: 0.18, delay: index * 0.01 }}
                      className={`group border-t border-slate-100 transition hover:bg-[#f7f5ff] ${
                        isDeleting ? "opacity-60" : ""
                      }`}
                    >
                      <td className="px-3 sm:px-4 py-2 font-medium">
                        {item?.diamondName || "—"}
                      </td>
                      <td className="px-3 sm:px-4 py-2">
                        {item?.shapeName || item?.shape?.name || item?.shape?.label || "—"}
                      </td>
                      <td className="px-3 sm:px-4 py-2">
                        {item?.colorName || item?.color?.name || item?.color?.label || "—"}
                      </td>
                      <td className="px-3 sm:px-4 py-2">
                        {item?.clarityName ||
                          item?.clarity?.name ||
                          item?.clarity?.label ||
                          "—"}
                      </td>
                      <td className="px-3 sm:px-4 py-2">
                        {item?.deductable ? "Yes" : "No"}
                      </td>

                      <td className="px-3 sm:px-4 py-2">
                        <span
                          className={[
                            "inline-flex rounded-full border px-2 py-0.5 text-[10px] font-semibold",
                            item?.isActive
                              ? "border-emerald-100 bg-emerald-50 text-emerald-700"
                              : "border-rose-100 bg-rose-50 text-rose-700",
                          ].join(" ")}
                        >
                          {item?.isActive ? "Active" : "Inactive"}
                        </span>
                      </td>

                      <td className="sticky right-0 z-10 w-[82px] min-w-[82px] whitespace-nowrap border-l border-violet-100 bg-violet-50 px-1 py-2 text-center shadow-[-6px_0_10px_-10px_rgba(15,23,42,0.35)] transition group-hover:bg-violet-100 sm:w-[104px] sm:min-w-[104px] sm:px-2">
                        <div className="inline-flex items-center justify-center gap-0.5 sm:gap-1">
                        {/* Edit */}
                        <motion.button
                          whileHover={{ scale: 1.05 }}
                          whileTap={{ scale: 0.95 }}
                          onClick={() => navigate(`/masters/diamond-master-list/${id}`)}
                          className="inline-flex h-6 w-6 items-center justify-center rounded-md text-[10px] text-[#6b59c6] hover:bg-violet-50 sm:h-7 sm:w-7 sm:rounded-lg sm:text-[11px]"
                          title="Edit"
                          aria-label="Edit diamond"
                        >
                          <FaPencilAlt />
                        </motion.button>

                        {/* Toggle */}
                        <button
                          onClick={() => onToggleActive(item)}
                          disabled={isToggling}
                          className={[
                            "inline-flex h-6 w-6 items-center justify-center rounded-md border text-[10px] disabled:opacity-50 sm:h-7 sm:w-7 sm:rounded-lg sm:text-[11px]",
                            item?.isActive
                              ? "border-amber-100 text-amber-600 hover:bg-amber-50"
                              : "border-emerald-100 text-emerald-600 hover:bg-emerald-50",
                          ].join(" ")}
                          title={item?.isActive ? "Deactivate" : "Activate"}
                          aria-label={item?.isActive ? "Deactivate diamond" : "Activate diamond"}
                        >
                          {isToggling ? <Spinner size={12} /> : <FaPowerOff />}
                        </button>

                        {/* Delete */}
                        <motion.button
                          whileHover={{ scale: 1.05 }}
                          whileTap={{ scale: 0.95 }}
                          onClick={() => openDelete(item)}
                          disabled={delState.isLoading}
                          className="inline-flex h-6 w-6 items-center justify-center rounded-md text-[10px] text-rose-500 hover:bg-rose-50 disabled:opacity-50 sm:h-7 sm:w-7 sm:rounded-lg sm:text-[11px]"
                          title="Delete"
                          aria-label="Delete diamond"
                        >
                          {isDeleting ? <Spinner size={12} /> : <FaTrashAlt />}
                        </motion.button>
                        </div>
                      </td>
                    </motion.tr>
                  );
                })}
            </AnimatePresence>
          </tbody>
        </table>
      </div>

      {/* Pagination */}
      <div className="flex shrink-0 items-center justify-between gap-2 rounded-b-2xl border-t bg-slate-50/60 px-2 py-1.5 text-[10px] sm:px-4 sm:py-2 sm:text-sm">
        <div className="hidden text-slate-600 sm:block">
          Page <span className="font-semibold">{page}</span> of{" "}
          <span className="font-semibold">{totalPages}</span> • {total ?? 0} record(s)
        </div>

        <div className="flex w-full items-center justify-between gap-2 sm:w-auto sm:justify-start">
          <div className="flex shrink-0 items-center gap-1 text-slate-600">
            <span className="hidden text-xs sm:inline">Rows / page</span>
            <Select
              value={{ value: limit, label: String(limit) }}
              options={[10, 20, 50, 100].map((number) => ({
                value: number,
                label: String(number),
              }))}
              onChange={(option) => {
                setLimit(Number(option?.value) || 10);
                setPage(1);
              }}
              isDisabled={isFetching || isLoading}
              isSearchable={false}
              menuPlacement="top"
              menuPosition="fixed"
              menuPortalTarget={document.body}
              menuShouldScrollIntoView={false}
              styles={paginationSelectStyles}
              className="w-[54px]"
              aria-label="Rows per page"
            />
            <span className="text-[9px] text-slate-400 sm:hidden">/ page</span>
          </div>

          <div className="flex min-w-0 flex-1 items-center justify-end gap-1.5 sm:flex-none sm:justify-start sm:gap-2">
            <button
              className="inline-flex h-8 w-8 items-center justify-center rounded-full border bg-white text-base hover:bg-white disabled:opacity-50 sm:h-auto sm:w-auto sm:px-3 sm:py-1 sm:text-sm"
              disabled={page <= 1 || isFetching || isLoading}
              onClick={() => setPage((p) => Math.max(1, p - 1))}
              aria-label="Previous page"
            >
              <span className="sm:hidden" aria-hidden="true">
                &lsaquo;
              </span>
              <span className="hidden sm:inline">Prev</span>
            </button>

            <span className="flex items-center gap-1 text-slate-600 sm:hidden">
              <Select
                value={{
                  value: totalPages ? page : 1,
                  label: String(totalPages ? page : 1),
                }}
                options={Array.from(
                  { length: Math.max(1, totalPages) },
                  (_, index) => ({
                    value: index + 1,
                    label: String(index + 1),
                  }),
                )}
                onChange={(option) => setPage(Number(option?.value) || 1)}
                isDisabled={isFetching || isLoading}
                isSearchable={false}
                menuPlacement="top"
                menuPosition="fixed"
                menuPortalTarget={document.body}
                menuShouldScrollIntoView={false}
                styles={paginationSelectStyles}
                className="w-[52px]"
                aria-label="Select page number"
              />
              <span>/ {Math.max(1, totalPages)}</span>
            </span>

            <span className="hidden items-center gap-1 text-slate-600 sm:flex">
              <span>Page</span>
              <Select
                value={{
                  value: totalPages ? page : 1,
                  label: String(totalPages ? page : 1),
                }}
                options={Array.from(
                  { length: Math.max(1, totalPages) },
                  (_, index) => ({
                    value: index + 1,
                    label: String(index + 1),
                  }),
                )}
                onChange={(option) => setPage(Number(option?.value) || 1)}
                isDisabled={isFetching || isLoading}
                isSearchable={false}
                menuPlacement="top"
                menuPosition="fixed"
                menuPortalTarget={document.body}
                menuShouldScrollIntoView={false}
                styles={paginationSelectStyles}
                className="w-[58px]"
                aria-label="Select page number"
              />
              <span>of {Math.max(1, totalPages)}</span>
            </span>

            <button
              className="inline-flex h-8 w-8 items-center justify-center rounded-full border bg-white text-base hover:bg-white disabled:opacity-50 sm:h-auto sm:w-auto sm:px-3 sm:py-1 sm:text-sm"
              disabled={page >= totalPages || isFetching || isLoading}
              onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
              aria-label="Next page"
            >
              <span className="sm:hidden" aria-hidden="true">
                &rsaquo;
              </span>
              <span className="hidden sm:inline">Next</span>
            </button>
          </div>
        </div>
      </div>

      {/* Filters Modal */}
      <AnimatePresence>
        {isFilterOpen && (
          <motion.div
            className="fixed inset-0 z-[9998] bg-black/40 flex items-center justify-center p-4"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
          >
            <motion.div
              className="bg-white rounded-2xl shadow-xl w-full max-w-lg max-h-[90vh] overflow-hidden flex flex-col"
              initial={{ scale: 0.9, y: 20, opacity: 0 }}
              animate={{ scale: 1, y: 0, opacity: 1 }}
              exit={{ scale: 0.9, y: 20, opacity: 0 }}
            >
              <div className="flex items-center justify-between px-4 py-3 border-b">
                <div>
                  <h3 className="text-sm font-semibold text-slate-800">
                    Advanced filters
                  </h3>
                  <p className="text-[11px] text-slate-500">Filter diamonds by status.</p>
                </div>
                <button
                  className="text-slate-500 hover:text-slate-700"
                  onClick={() => setIsFilterOpen(false)}
                >
                  <FaTimesCircle />
                </button>
              </div>

              <div className="p-4 space-y-4 text-[11px] overflow-y-auto">
                <div className="grid grid-cols-1 gap-3">
                  <div>
                    <label className="block text-slate-600 mb-1">Status</label>
                    <select
                      value={tempFilters.status}
                      onChange={(e) =>
                        setTempFilters((f) => ({ ...f, status: e.target.value }))
                      }
                      className="w-full px-3 py-2 border border-slate-200 rounded-lg focus:outline-none focus:ring-1 focus:ring-violet-400 bg-white"
                    >
                      <option value="">All</option>
                      <option value="Active">Active</option>
                      <option value="Inactive">Inactive</option>
                    </select>
                  </div>
                </div>
              </div>

              <div className="px-4 py-3 border-t bg-slate-50 flex items-center justify-between">
                <button
                  type="button"
                  onClick={() => setTempFilters({ status: "" })}
                  className="inline-flex items-center gap-1 text-[11px] text-slate-600 hover:text-slate-800"
                >
                  <FiRotateCcw />
                  Reset
                </button>

                <div className="flex items-center gap-2">
                  <button
                    type="button"
                    onClick={() => setIsFilterOpen(false)}
                    className="px-3 py-1.5 rounded-md border border-slate-200 text-[11px] text-slate-700 bg-white hover:bg-slate-100"
                  >
                    Close
                  </button>
                  <button
                    type="button"
                    onClick={() => {
                      setFilters(tempFilters);
                      setPage(1);
                      setIsFilterOpen(false);
                    }}
                    className="px-3 py-1.5 rounded-md text-[11px] text-white bg-[#6b59c6] hover:opacity-95"
                  >
                    Apply
                  </button>
                </div>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Delete confirm */}
      <ConfirmDialog
        open={deleteDlg.open}
        onCancel={closeDelete}
        onConfirm={confirmDelete}
        title={`Delete "${deleteDlg.row?.diamondName || ""}"?`}
        message="Are you sure, Deleted item can't be retrived."
        confirmText="Delete"
        cancelText="Cancel"
        loading={delState.isLoading}
        destructive
      />
    </div>
  );
}
